/**
 * The application frame: a drawer that is a sidebar on a wide screen.
 *
 * daisyUI's `drawer` with `lg:drawer-open` is one element that behaves as an
 * overlay below the breakpoint and a pinned column above it, so there is no
 * second mobile navigation to keep in step with the first.
 *
 * @module components/ui/AppShell
 */

import React, { type ReactNode, useCallback, useEffect, useRef } from "react";

import { IconButton } from "./Button";
import { SkipLink } from "./Navigation";

export interface AppShellProps {
  /** The sidebar's contents. */
  sidebar: ReactNode;
  /** The bar across the top of the content column. */
  topbar: ReactNode;
  /** Sits above the content, for an impersonation or read-only notice. */
  banner?: ReactNode;
  children: ReactNode;
  /** Controls the drawer from outside, for a route change that should close it. */
  drawerId?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <AppShell sidebar={<Sidebar …/>} topbar={<Topbar …/>}>{outlet}</AppShell>
 * ```
 */
export function AppShell({
  sidebar,
  topbar,
  banner = undefined,
  children,
  drawerId = "nav-drawer",
  "data-testid": testId,
}: AppShellProps): React.ReactElement {
  const contentRef = useRef<HTMLDivElement>(null);
  const skipRef = useRef<HTMLAnchorElement>(null);

  const close = useCallback(
    (restoreFocus = true) => {
      const toggle = document.getElementById(drawerId);
      // Nothing to do when it is already shut. Escape belongs to whatever is
      // open, so acting anyway would pull focus to the hamburger from the modal
      // or the field the key was actually meant for.
      if (!(toggle instanceof HTMLInputElement) || !toggle.checked) {
        return;
      }
      const returnTo =
        toggle.parentElement?.querySelector<HTMLElement>("[data-drawer-open]") ?? null;
      toggle.checked = false;
      // Before the focus call below: `returnTo` lives in the content column,
      // and focus() is discarded inside an inert subtree.
      toggle.dispatchEvent(new Event("change"));
      // Focus comes back with it. daisyUI hides `.drawer-side` when the toggle
      // unchecks, so the browser blurs whatever inside it held focus and drops
      // it to the body — the next Tab then restarts at the skip link instead of
      // at the control they opened the drawer with. A navigation goes to the
      // page instead of the hamburger: nothing else in this app moves focus on
      // a route change, so without this it lands on the body just the same.
      if (restoreFocus) {
        returnTo?.focus();
        return;
      }
      document.getElementById("main-content")?.focus();
    },
    [drawerId],
  );

  // Following a link inside the drawer closes it. Below `lg` the drawer covers
  // the page, so navigating with it open leaves the reader looking at the
  // navigation they just used rather than at the page they asked for.
  useEffect(() => {
    const onClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      // Only a link, and only one inside the drawer: a control that merely
      // opens a submenu has not gone anywhere yet.
      const link = target?.closest("a[href]");
      if (!link || !target?.closest(".drawer-side")) {
        return;
      }
      // And only a click that replaces this page. A modified or middle click
      // opens a new tab and leaves the reader here, as does target="_blank",
      // so closing the navigation under them shuts what they are still using.
      const newTab =
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        link.getAttribute("target") === "_blank";
      if (!newTab) {
        close(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("click", onClick);
    };
  }, [close]);

  // Below `lg` the drawer overlays the page, so what is behind it leaves the
  // focus order rather than staying reachable behind the scrim. The media query
  // matters because above `lg` the sidebar is permanent and the checkbox can
  // still read checked after a resize, which would strand the whole app.
  useEffect(() => {
    const toggle = document.getElementById(drawerId);
    if (!(toggle instanceof HTMLInputElement)) {
      return undefined;
    }
    // Optional: jsdom ships no matchMedia, and a shell that throws on mount
    // because a media query is unavailable is worse than one that never marks
    // the page inert. Absent, it behaves as the permanent-sidebar case.
    const overlay =
      typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 63.999rem)") : null;
    const sync = (): void => {
      const content = contentRef.current;
      if (!content) {
        return;
      }
      // Growing past the breakpoint retires the overlay, so the drawer's own
      // state goes with it. Left checked it is invisible against the pinned
      // sidebar, then springs back over the page when the viewport narrows.
      if (toggle.checked && overlay && !overlay.matches) {
        toggle.checked = false;
      }
      // The skip link with it. It sits ahead of the drawer so that it is the
      // first tab stop, which puts it outside the column that goes inert —
      // and its target is inside that column, so left live it is a control
      // that reads as the way out and does nothing.
      const behind = [content, skipRef.current].filter((node) => node !== null);
      if (toggle.checked && overlay?.matches) {
        behind.forEach((node) => node.setAttribute("inert", ""));
      } else {
        behind.forEach((node) => node.removeAttribute("inert"));
      }
    };
    toggle.addEventListener("change", sync);
    overlay?.addEventListener("change", sync);
    sync();
    return () => {
      toggle.removeEventListener("change", sync);
      overlay?.removeEventListener("change", sync);
    };
  }, [drawerId]);

  // Escape closes the overlay. daisyUI's drawer is a checkbox and a label, and
  // neither of those listens for a key.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close]);

  return (
    <div className="drawer lg:drawer-open" data-testid={testId}>
      {/* First in the DOM, not first in the content column. daisyUI places the
          two columns with `grid-column-start`, so the sidebar paints on the
          left wherever it sits in the markup, and above `lg` it is visible and
          focusable: a skip link after it skips what has already been walked.
          `sr-only` and `focus:fixed` both take this out of flow, so it never
          becomes a grid item. */}
      <SkipLink ref={skipRef} />
      {/* daisyUI only display:none's this above `lg`. Below that it is a
          zero-size transparent input that is still tabbable and still named,
          so it would be the first stop on every page and a second control
          announced for opening the drawer. The topbar button drives it. */}
      <input
        id={drawerId}
        type="checkbox"
        className="drawer-toggle"
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="drawer-side z-[70]">
        {/* Pointer-only, and hidden from assistive technology: it duplicates
            the close button below, and two controls with one name is worse
            than one. The keyboard closes with Escape or that button. */}
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => {
            close();
          }}
          // `block`, because a button is inline-block by default. Above `lg`
          // daisyUI drops `.drawer-side` from grid to block, so an inline
          // overlay stops sharing the sidebar's cell and instead contributes a
          // line box that pushes the sidebar down and cuts off its footer.
          className="drawer-overlay block"
        />
        <div className="relative">
          <IconButton
            icon="x"
            aria-label="Close navigation"
            size="sm"
            className="absolute top-4 right-4 z-[1] lg:hidden"
            onClick={() => {
              close();
            }}
            // What the top bar's open button moves focus to. Without a marker
            // it took the first match in document order, which is the
            // aria-hidden overlay sitting above this in the DOM.
            data-drawer-close=""
            data-testid="close-navigation"
          />
          {sidebar}
        </div>
      </div>
      {/* max-lg:isolate keeps the sticky top bar from painting over the open
          drawer on a narrow screen. */}
      <div
        ref={contentRef}
        className="drawer-content max-lg:isolate flex min-h-[100dvh] flex-col bg-base-100"
      >
        {topbar}
        {/* The banner is an alert, so it carries the box radius. Full-bleed
            against the topbar and the drawer that radius reads as a mistake:
            give it the page gutter and let it sit as the card it is. */}
        {banner && (
          <div className="mx-auto w-full max-w-[1380px] px-8 pt-4 max-lg:px-4 max-sm:px-3">
            {banner}
          </div>
        )}
        {/* tabIndex -1 so it can take focus without becoming a tab stop: it is
            the skip link's target and where focus lands after the drawer
            navigates. */}
        <main id="main-content" tabIndex={-1} className="flex min-w-0 flex-1 flex-col">
          {children}
        </main>
      </div>
    </div>
  );
}
